/* eslint-disable no-undefined */
import { sentryLogger } from '../../../monitor/monitor';
import { CollaborativeSpace } from '../collaborative_space/collaborative_space.ts';

class LinksStorage {
  static links = new Map();
  static updatedLinks = new Map();
  static initialized = false;
  static linkedTasks = null;
  static autoSchedule = true;
  static cache = new Map();
  static eventIds = [];
  static hasChanges = false;
  static storageRef = null;
  static deletedLinks = new Map();
  static initialLinks = new Set();

  static init(gantt) {
    if (!gantt?.getDatastore || !gantt.initAlreadyDid) {
      return;
    }

    const allLinks = gantt.getLinks();
    if (allLinks.length === 0) {
      return;
    }

    if (this.initialized) {
      return;
    }

    const storage = gantt.getDatastore('link');
    this.storageRef = storage;
    this.initialized = true;

    // Store initial links
    this.initialLinks.clear();
    allLinks.forEach((link) => {
      this.initialLinks.add(link.id);
    });

    const pullStorage = storage.pull;
    const linksId = Object.keys(pullStorage);

    if (linksId.length > 0) {
      this.infectCurrentDataInProxy(pullStorage, linksId);
    }

    storage.pull = this.infectLinksContainer(pullStorage);

    const onStoreUpdatedId = storage.attachEvent(
      'onStoreUpdated',
      this._handleStoreUpdated.bind(this, gantt)
    );

    const onAfterAddId = storage.attachEvent(
      'onAfterAdd',
      this._handleAfterAdd.bind(this)
    );

    const onAfterDeleteId = storage.attachEvent(
      'onAfterDelete',
      this._handleAfterDelete.bind(this)
    );

    const onBeforeTaskDeleteId = gantt.attachEvent(
      'onBeforeTaskDelete',
      this._handleBeforeTaskDelete.bind(this, gantt)
    );

    this.eventIds.push(
      onStoreUpdatedId,
      onAfterAddId,
      onAfterDeleteId,
      onBeforeTaskDeleteId
    );
  }
  static _handleStoreUpdated(gantt, id, item, action) {
    if (shouldAvoidUpdateStorage(action, item, gantt)) {
      return;
    }

    if (!this.hasChanges) {
      this.hasChanges = true;
    }

    if (action === ACTIONS.DELETE) {
      this.links.delete(id);
    }

    this.updatedLinks.set(id, item);
    this.updateAutoSchedule(true);
    CollaborativeSpace.notifyIsEditing();
  }

  static _handleAfterAdd(id, item) {
    this.links.set(id, item);
    this.autoSchedule = true;
  }

  static updateLinkedTasks(linkedTasks) {
    this.linkedTasks = linkedTasks;
    this.autoSchedule = false;
  }

  static updateAutoSchedule(autoSchedule = true) {
    this.autoSchedule = autoSchedule;
  }

  /**
   * Handles the deletion of a link by storing its complete structure and removing it from active links
   * @param {string} id - The unique identifier of the deleted link
   * @param {Object} item - The complete link object containing all its properties
   */
  static _handleAfterDelete(id, item) {
    if (!item || typeof item !== 'object' || Object.keys(item).length === 0) {
      return;
    }

    if (!item.source || !item.target || item.type === undefined) {
      return;
    }

    if (this.initialLinks.has(id)) {
      this.deletedLinks.set(id, { ...item });
    }

    this.links.delete(id);
  }

  /**
   * Handles task deletion by capturing associated links before they are deleted silently
   * @param {Object} gantt - The gantt instance
   * @param {string} taskId - The ID of the task being deleted
   */
  static _handleBeforeTaskDelete(gantt, taskId) {
    const allLinks = gantt.getLinks();
    const connectedLinks = allLinks.filter(
      (link) =>
        link.source.toString() === taskId.toString() ||
        link.target.toString() === taskId.toString()
    );

    connectedLinks.forEach((link) => {
      if (link && link.id) {
        this.deletedLinks.set(link.id, { ...link });
        this.links.delete(link.id);
      }
    });
  }

  static getLinkedTasks() {
    return this.linkedTasks;
  }

  /**
   * Retrieves all deleted links with their complete structure
   * @returns {Array<Object>} Array of deleted link objects with their complete structure
   */
  static getDeletedLinks() {
    const deletedLinksArray = Array.from(this.deletedLinks.values());

    const validDeletedLinks = deletedLinksArray.filter((link) => {
      const isValid =
        link &&
        typeof link === 'object' &&
        Object.keys(link).length > 0 &&
        link.id &&
        (link.source || link.source === 0) &&
        (link.target || link.target === 0) &&
        link.type !== undefined;

      return isValid;
    });

    return validDeletedLinks;
  }

  static shouldRecalculateLinkedTasks() {
    return this.autoSchedule;
  }

  static invalidateCache() {
    this.cache = new Map();
  }

  static setCache(id = 'global', relations) {
    if (
      !Array.isArray(relations) ||
      !relations.some(Boolean) ||
      relations.length <= 1
    ) {
      return;
    }

    this.cache.set(id, [...relations]);
  }

  static getCache(id = 'global') {
    return [...this.cache.get(id)];
  }

  static isCachedActive(id = 'global') {
    return this.cache.has(id);
  }

  static infectCurrentDataInProxy(storage, linksId) {
    linksId.forEach(
      (id) =>
        (storage[id] = new Proxy(storage[id], {
          set: (target, prop, value, receiver) => {
            LinksStorage.invalidateCache();
            return Reflect.set(target, prop, value, receiver);
          }
        }))
    );
  }

  static infectLinksContainer(storage) {
    return new Proxy(storage, {
      get: (target, prop) => Reflect.get(target, prop),
      set: (target, prop, value, receiver) => {
        LinksStorage.invalidateCache();
        const proxiedValue = new Proxy(value, {
          set(innerTarget, innerProp, innerValue) {
            LinksStorage.invalidateCache();
            return Reflect.set(innerTarget, innerProp, innerValue);
          }
        });

        return Reflect.set(target, prop, proxiedValue, receiver);
      },
      deleteProperty: (target, prop) => {
        LinksStorage.invalidateCache();
        return Reflect.deleteProperty(target, prop);
      }
    });
  }

  static removeEvents() {
    const storage = this.storageRef;
    if (!storage) return;

    this.eventIds.forEach((eventId) => {
      storage.detachEvent(eventId);
    });
    this.eventIds = [];
  }

  /**
   * Resets all storage collections to their initial state
   * Clears links, updatedLinks, deletedLinks, and invalidates the cache
   */
  static reset() {
    this.links.clear();
    this.updatedLinks.clear();
    this.hasChanges = false;
    this.invalidateCache();
    this.deletedLinks.clear();
    this.initialLinks.clear();
  }

  static reinit() {
    const gantt = window.to_use_react_gantt;
    this.reset();
    this.removeEvents();
    this.initialized = false;

    if (gantt) {
      this.init(gantt);
    }
  }

  static kill() {
    try {
      this.reset();
      this.removeEvents();
      this.initialized = false;
      this.storageRef = null;
    } catch (e) {
      sentryLogger('Error on LinksStorage kill', e);
    } finally {
      this.initialized = false;
    }
  }

  static checkHasChanges() {
    return this.hasChanges;
  }
}

const ACTIONS = {
  DELETE: 'delete',
  PAINT: 'paint'
};

function shouldAvoidUpdateStorage(action, item, gantt) {
  const { initAlreadyDid, is_autoscheduling } = gantt;

  if (!action || action === ACTIONS.PAINT) {
    return true;
  }

  if (item === false) {
    return true;
  }

  if (!initAlreadyDid || is_autoscheduling) {
    return true;
  }

  return false;
}

export { LinksStorage };
